// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import type { ColumnDef } from "@tanstack/react-table"
import { KeyRound, Trash2 } from "lucide-react"
import { defineMeta } from "@/lib/column-meta"
import { formatDate } from "@/utils"
import type { ApiKey } from "@/queries/queries.apikeys"

/**
 * The column model of the API-keys table — masked value, creation and
 * expiration dates (with a discreet "expired" marker when past) and the
 * revoke affordance, rendered through the shared Lagon `SmartTable`.
 *
 * A factory rather than a const array because the revoke cell closes over
 * the caller's `onRevoke`. Sorting is disabled: the list is small and the
 * view keeps its own newest-first order (no URL-owned sort state).
 */

/** True when the key's expiration date is in the past. */
const isExpired = (key: ApiKey): boolean =>
  !!key.expirationDate && new Date(key.expirationDate).getTime() < Date.now()

export const apiKeyColumns = (
  onRevoke: (key: ApiKey) => void,
): ColumnDef<ApiKey>[] => [
  {
    id: "key",
    header: "Key",
    enableSorting: false,
    cell: ({ row }) => {
      const expired = isExpired(row.original)
      return (
        <span className="inline-flex min-w-0 items-center gap-2.5">
          <KeyRound
            size={14}
            strokeWidth={2}
            className={`shrink-0 ${expired ? "text-text-tertiary" : "text-primary"}`}
          />
          <span
            className={`select-text overflow-hidden text-ellipsis whitespace-nowrap font-mono ${
              expired ? "text-text-tertiary" : "text-text-secondary"
            }`}
          >
            {row.original.key}
          </span>
        </span>
      )
    },
    meta: defineMeta<ApiKey>({ width: 1.6 }),
  },
  {
    id: "created",
    header: "Created",
    enableSorting: false,
    cell: ({ row }) => (
      <span className="font-mono text-text-tertiary">
        {formatDate(row.original.createdAt ?? "")}
      </span>
    ),
    meta: defineMeta<ApiKey>({ width: 1 }),
  },
  {
    id: "expires",
    header: "Expires",
    enableSorting: false,
    cell: ({ row }) => {
      const expired = isExpired(row.original)
      return (
        <span
          className={`inline-flex items-center gap-[7px] font-mono ${
            expired ? "text-expired-fg" : "text-text-tertiary"
          }`}
        >
          {formatDate(row.original.expirationDate ?? "")}
          {expired && (
            <span className="rounded-full border border-expired/40 bg-expired/10 px-[7px] py-px text-[9.5px] text-expired-fg">
              expired
            </span>
          )}
        </span>
      )
    },
    meta: defineMeta<ApiKey>({ width: 1 }),
  },
  {
    id: "action",
    header: "Action",
    enableSorting: false,
    cell: ({ row }) => (
      <button
        onClick={() => onRevoke(row.original)}
        className="inline-flex h-[30px] items-center gap-1.5 rounded-[8px] border border-destructive/30 px-[11px] text-[11.5px] text-destructive transition-colors hover:border-destructive/60 hover:bg-destructive/10"
      >
        <Trash2 size={12} strokeWidth={2} />
        Revoke
      </button>
    ),
    meta: defineMeta<ApiKey>({ width: 0.7, align: "right" }),
  },
]
